import React from "react";
import { FormGroup, Label } from "reactstrap";
import { Controller } from "react-hook-form";
import { AntInput } from "../../../CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { useTranslation } from "react-i18next";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions/SelectCachedOptions";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { useEmployeesOptionsQuery } from "@/services/basicData/basicData";

export const ServiceCenterForm = (props) => {
  const { control, errors, system } = props;
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const isMadina = system === "madina";
  const { debouncedValue, setSearchValue } = useDebouncedSearch("", 500);
  const { data: employeesRes, isLoading: employeesLoading } = useEmployeesOptionsQuery({
    ...(debouncedValue ? { search: debouncedValue } : {}),
  });
  const employees = employeesRes?.data?.data?.data ?? employeesRes?.data?.data ?? [];

  return (
    <>
      <FormGroup className="mb-3">
        <Controller
          control={control}
          name="name_ar"
          rules={{
            required: t("SERVICE_CENTER_NAME_REQUIRED"),
          }}
          render={({ field }) => (
            <>
              <Label for="name_ar">{t("NAME_AR")}</Label>
              <AntInput
                type="text"
                id="name_ar"
                name="name_ar"
                {...field}
                placeholder={t("SERVICE_CENTER_NAME_PLACEHOLDER")}
                status={errors.name_ar ? "error" : undefined}
              />
              {errors.name_ar && (
                <div className="text-danger">{errors.name_ar.message}</div>
              )}
            </>
          )}
        />
      </FormGroup>
      <FormGroup className="mb-3">
        <Controller
          control={control}
          name="name_en"
          rules={{
            required: t("SERVICE_CENTER_NAME_REQUIRED"),
          }}
          render={({ field }) => (
            <>
              <Label for="name_en">{t("NAME_EN")}</Label>
              <AntInput
                type="text"
                id="name_en"
                name="name_en"
                {...field}
                placeholder={t("SERVICE_CENTER_NAME_PLACEHOLDER")}
                status={errors.name_en ? "error" : undefined}
              />
              {errors.name_en && (
                <div className="text-danger">{errors.name_en.message}</div>
              )}
            </>
          )}
        />
      </FormGroup>
      <FormGroup className="mb-3">
        <Controller
          control={control}
          name="code"
          rules={{
            required: t("SERVICE_CENTER_CODE_REQUIRED"),
            pattern: {
              value: /^[a-zA-Z0-9_-]+$/,
              message: t("SERVICE_CENTER_CODE_INVALID"),
            },
          }}
          render={({ field }) => (
            <>
              <Label for="code">{t("SERVICE_CENTER_CODE")}</Label>
              <AntInput
                type="text"
                id="code"
                name="code"
                {...field}
                placeholder={t("SERVICE_CENTER_CODE_PLACEHOLDER")}
                status={errors.code ? "error" : undefined}
              />
              {errors.code && (
                <div className="text-danger">{errors.code.message}</div>
              )}
            </>
          )}
        />
      </FormGroup>
      <FormGroup className="mb-3">
        <Controller
          control={control}
          name="address"
          render={({ field }) => (
            <>
              <Label for="address">{t("ADDRESS")}</Label>
              <AntInput
                type="text"
                id="address"
                name="address"
                {...field}
                placeholder={t("ADDRESS")}
                status={errors.address ? "error" : undefined}
              />
              {errors.address && (
                <div className="text-danger">{errors.address.message}</div>
              )}
            </>
          )}
        />
      </FormGroup>
      <FormGroup className="mb-3">
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <>
              <Label for="phone">{t("PHONE")}</Label>
              <AntInput
                type="text"
                id="phone"
                name="phone"
                {...field}
                placeholder={t("PHONE")}
                status={errors.phone ? "error" : undefined}
              />
              {errors.phone && (
                <div className="text-danger">{errors.phone.message}</div>
              )}
            </>
          )}
        />
      </FormGroup>
      <FormGroup className="mb-3">
        <Controller
          control={control}
          name="manager_id"
          render={({ field }) => (
            <>
              <Label for="manager_id">{t("RESPONSIBLE_EMPLOYEE")}</Label>
              <AntSelect
                id="manager_id"
                showSearch
                allowClear
                filterOption={false}
                onSearch={setSearchValue}
                loading={employeesLoading}
                placeholder={t("SELECT_RESPONSIBLE_EMPLOYEE")}
                value={field.value ?? undefined}
                onChange={(val) => field.onChange(val ?? null)}
                options={employees.map((employee) => ({
                  value: employee.id,
                  label:
                    [
                      employee.id,
                      employee.name ??
                        getLocalizedText(employee.name_ar, employee.name_en),
                      employee.phone,
                    ]
                      .filter(Boolean)
                      .join(" - ") ||
                    employee.email ||
                    String(employee.id),
                }))}
                style={{ width: "100%" }}
              />
              {errors.responsible_employee_id && (
                <div className="text-danger">
                  {errors.responsible_employee_id.message}
                </div>
              )}
            </>
          )}
        />
      </FormGroup>
      {isMadina && (
        <FormGroup className="mb-3">
          <Controller
            control={control}
            name="nationality_id"
            render={({ field }) => (
              <>
                <Label for="nationality_id">{t("NATIONALITY")}</Label>
                <SelectCachedOptions
                  id="nationality_id"
                  optionKey="nationalities"
                  placeholder={t("CHOOSE")}
                  showSearch
                  allowClear
                  {...field}
                  value={field.value ?? undefined}
                  onChange={(val) => field.onChange(val ?? null)}
                  options={(n) => ({
                    value: n.id,
                    label:
                      getLocalizedText(n.name_ar, n.name_en) ??
                      getLocalizedText(n.nationalityNameAr, n.nationalityNameEn) ??
                      n.name ??
                      "",
                  })}
                  style={{ width: "100%" }}
                />
              </>
            )}
          />
        </FormGroup>
      )}
    </>
  );
};
